/** iter318 wave 15 — Home page. CMS-driven when Super Admin has published
 *  sections for the "home" page; otherwise falls back to the static
 *  wave-13 layout so first-run installs still render a polished page. */
import { Link } from 'react-router-dom';
import { useWebsiteContent } from '../../../hooks/useWebsiteContent';
import SectionRenderer from './SectionRenderer';
import { usePageSeo } from './usePageSeo';

const Section = ({ eyebrow, title, subtitle, children, tone = 'light', id }) => (
  <section id={id} className={tone === 'tint' ? 'bg-[#EAF5FF]' : 'bg-white'}>
    <div className="max-w-6xl mx-auto px-6 py-16 md:py-24">
      {eyebrow && <div className="text-xs uppercase tracking-[0.15em] font-semibold text-[#1677FF] mb-3">{eyebrow}</div>}
      {title && <h2 className="text-3xl md:text-4xl font-bold tracking-tight mb-3">{title}</h2>}
      {subtitle && <p className="text-lg text-[#566579] max-w-3xl mb-8">{subtitle}</p>}
      {children}
    </div>
  </section>
);

const Card = ({ title, body }) => (
  <div className="bg-white border border-[#DCE6F0] rounded-2xl p-6 shadow-sm hover:shadow-md transition">
    <h3 className="font-semibold text-lg mb-2">{title}</h3>
    <p className="text-sm text-[#566579] leading-relaxed">{body}</p>
  </div>
);

export default function VyomosHome() {
  const { content, loading } = useWebsiteContent('home');
  usePageSeo('home');
  const cmsSections = content?.sections || [];

  // While the first fetch is in-flight, avoid a jarring flash — the fallback
  // static content renders instantly if the CMS is empty (common on fresh
  // installs). Only defer the render when we're truly waiting on network.
  if (loading && !content) {
    return <div className="min-h-[60vh]" data-testid="vyomos-home-loading" />;
  }

  if (cmsSections.length > 0) {
    return (
      <div data-testid="vyomos-home-cms">
        {cmsSections.map((s) => <SectionRenderer key={s.id} section={s} />)}
      </div>
    );
  }

  return <StaticHome />;
}

function StaticHome() {
  return (
    <div data-testid="vyomos-home-static">
      {/* Hero (Spec §7.1) */}
      <section className="relative bg-gradient-to-b from-[#EAF5FF] via-[#F8FBFF] to-white">
        <div className="max-w-6xl mx-auto px-6 py-20 md:py-28 text-center">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white border border-[#DCEEFF] text-[#1677FF] text-xs font-medium mb-6">
            <span className="h-1.5 w-1.5 rounded-full bg-[#55AEEF] animate-pulse" />
            VyomOS for academies and facilities
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight mb-6 leading-[1.05]">
            The operating system for<br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#1677FF] to-[#55AEEF]">modern academies and facilities.</span>
          </h1>
          <p className="text-lg text-[#566579] max-w-2xl mx-auto mb-10">
            VyomOS gives growing businesses one operating space to manage registrations,
            scheduling, payments, communication, facility bookings, reporting, and growth.
          </p>
          <div className="flex flex-wrap items-center justify-center gap-3">
            <a href="https://demo.vyomos.io" data-testid="hero-primary-cta"
               className="px-6 py-3 rounded-full bg-[#1677FF] hover:bg-[#0e5fd6] text-white font-semibold transition">
              Experience VyomOS
            </a>
            <Link to="/contactus" data-testid="hero-secondary-cta"
                  className="px-6 py-3 rounded-full border border-[#DCE6F0] hover:border-[#55AEEF] bg-white transition">
              Book a Demo
            </Link>
            <Link to="/login" data-testid="hero-tertiary-cta"
               className="px-6 py-3 rounded-full text-[#566579] hover:text-[#10233F] transition">
              Customer Login →
            </Link>
          </div>
          <p className="text-xs text-[#566579] mt-8">
            Built for academy owners, facility operators, program directors, and multi-location businesses.
          </p>
        </div>
      </section>

      {/* Why Vyom (§7.2) */}
      <Section eyebrow="Why Vyom" title="Software with room to grow." tone="light">
        <p className="text-base text-[#566579] leading-relaxed max-w-3xl">
          Vyom means sky, space, and vastness — a symbol of openness, scale, and limitless potential.
          VyomOS brings that same openness to business operations, giving growing teams the space to
          manage, automate, and scale without being constrained by disconnected tools or rigid legacy software.
        </p>
      </Section>

      {/* Problem (§7.3) */}
      <Section title="Your business has grown. Your systems have not." tone="tint"
               subtitle="Academies and facilities often rely on spreadsheets, payment tools, messaging apps, booking systems, and manually prepared reports. The result is duplicated work, incomplete information, and limited visibility.">
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          <Card title="Scattered information" body="Customer, participant, program, payment, and facility information lives in different systems." />
          <Card title="Manual administration" body="Teams repeatedly process registrations, reminders, payments, attendance, and schedule changes." />
          <Card title="Disconnected communication" body="Important updates are spread across email, messaging applications, and individual conversations." />
          <Card title="Limited reporting" body="Operators struggle to see revenue, attendance, capacity, and facility utilization in one place." />
          <Card title="Slow customization" body="Legacy platforms rarely adapt to the way each business operates." />
          <Card title="VyomOS brings these workflows into one intelligent operating space." body="One connected operating system for everything above." />
        </div>
      </Section>

      {/* Pillars (§7.4) */}
      <Section title="Manage, automate, report, and scale from one operating space.">
        <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-4">
          {[
            ['Manage','CRM, registrations, classes, teams, coaches, customers, facilities, and users.'],
            ['Automate','Payments, reminders, communication, follow-ups, scheduling workflows, and recurring administration.'],
            ['Report','Revenue, attendance, enrollment, class capacity, booking utilization, and operational trends.'],
            ['Scale','Multi-location operations, custom domains, branded portals, permissions, and configurable workflows.'],
            ['Extend','Add custom modules and capabilities as the business evolves.'],
          ].map(([t, b]) => <Card key={t} title={t} body={b} />)}
        </div>
      </Section>

      {/* Modules (§7.5) */}
      <Section title="One platform for the work that keeps your business running." tone="tint">
        <div className="grid md:grid-cols-2 gap-4">
          {[
            ['Customer & Participant Management','Maintain connected profiles for families, players, customers, members, and participants.'],
            ['Programs & Registrations','Create programs, manage enrollments, assign groups, and track participation.'],
            ['Scheduling & Attendance','Coordinate classes, coaches, facilities, teams, attendance, and schedule changes.'],
            ['Payments & Billing','Manage recurring plans, invoices, payment methods, transaction history, and outstanding balances.'],
            ['Communication','Send relevant updates based on location, program, team, class, or customer group.'],
            ['Facility Booking','Manage court, lane, room, field, and facility availability through one scheduling system.'],
            ['Dashboards & Reporting','Monitor revenue, enrollment, attendance, utilization, capacity, and operational performance.'],
            ['White-Label Portals','Provide customers with a branded portal using their organization\u2019s domain, identity, and workflows.'],
          ].map(([t, b]) => <Card key={t} title={t} body={b} />)}
        </div>
      </Section>

      {/* AI (§7.7) */}
      <Section eyebrow="Practical intelligence" title="AI-powered insights built into everyday operations."
               subtitle="VyomOS uses AI to help operators interpret information, prepare communication, identify trends, and make faster decisions. It supports your team — it does not replace it.">
        <div className="bg-white border border-[#DCE6F0] rounded-2xl p-6 shadow-sm max-w-2xl" data-testid="ai-panel">
          <div className="text-xs text-[#566579] mb-2">Ask Vyom</div>
          <div className="text-sm text-[#10233F] font-medium mb-2">Which weekday programs have available capacity?</div>
          <div className="text-sm text-[#566579] border-l-2 border-[#55AEEF] pl-3">
            Wednesday\u2019s 5:30 PM beginner program is at 62% capacity. Saturday morning programs are above 90%.
          </div>
        </div>
      </Section>

      {/* Pricing preview (§7.11) */}
      <Section title="Plans designed to grow with your operation." tone="tint"
               subtitle="Start with the tools your organization needs today and expand as your programs, locations, and operational requirements grow.">
        <div className="flex flex-wrap gap-3">
          <Link to="/pricevyomos" className="px-6 py-3 rounded-full bg-[#1677FF] hover:bg-[#0e5fd6] text-white font-semibold" data-testid="home-pricing-cta">View Full Pricing</Link>
          <a href="https://demo.vyomos.io" className="px-6 py-3 rounded-full border border-[#DCE6F0] bg-white hover:border-[#55AEEF]">Experience VyomOS</a>
          <Link to="/contactus" className="px-6 py-3 rounded-full text-[#566579] hover:text-[#10233F]">Contact Sales →</Link>
        </div>
      </Section>

      {/* Final CTA (§7.13) */}
      <Section title="See how VyomOS can fit your operation."
               subtitle="Explore the product yourself or schedule a personalized conversation about your programs, facilities, workflows, and growth plans.">
        <div className="flex flex-wrap gap-3">
          <a href="https://demo.vyomos.io" className="px-6 py-3 rounded-full bg-[#1677FF] hover:bg-[#0e5fd6] text-white font-semibold">Experience VyomOS</a>
          <Link to="/contactus" className="px-6 py-3 rounded-full border border-[#DCE6F0] bg-white hover:border-[#55AEEF]">Book a Personalized Demo</Link>
          <Link to="/login" className="px-6 py-3 rounded-full text-[#566579] hover:text-[#10233F]">Customer Login →</Link>
        </div>
      </Section>
    </div>
  );
}
